<template>
    <div class="oxygen-manage">
        <ModalHeader>
            <h2>산소발생기 관리</h2>
        </ModalHeader>

        <SearchSection>
            <SearchGroup>
                <SearchInput v-model="searchQuery" placeholder="관리번호, 제품명, 대여자명 검색" @keyup.enter="fetchOxygenList" />
                <HanqBtn @click="fetchOxygenList">
                    <Magnify />
                </HanqBtn>
            </SearchGroup>
            <ButtonContainer>
                <RegisterButton @click="downloadApplicationForm">산소발생기 등록신청서 양식</RegisterButton>
                <RegisterButton @click="openNHISLink">공단 산소발생기 목록</RegisterButton>
                <RegisterButton @click="openRegisterModal">산소발생기 등록</RegisterButton>
                <RegisterButton v-if="$globalState.company_key === 32" @click="downloadOxygenList">산소발생기 목록 다운로드
                </RegisterButton>
            </ButtonContainer>
        </SearchSection>

        <TableContainer>
            <OxygenTable>
                <colgroup>
                    <col />
                    <col />
                    <col />
                    <col />
                    <col />
                    <col />
                    <col />
                    <col />
                    <col />
                </colgroup>
                <thead>
                    <tr>
                        <th>순번</th>
                        <th>관리번호</th>
                        <th>제품명</th>
                        <th>상태</th>
                        <th>구분</th>
                        <th>급여/비급여</th>
                        <th>대여자</th>
                        <th>대여시작일</th>
                        <th>대여종료일</th>
                        <th>등록해제</th>
                    </tr>
                </thead>
                <tbody>
                    <StyledTableRow v-for="(item, index) in oxygenators" :key="item.num" @click="openEditModal(item)"
                        :status="item.oxy_rental_name === '매장' ? '매장' : item.oxy_rental_status"
                        :class="{ 'blurred': isTestUser }">
                        <td>{{ index + 1 }}</td>
                        <td>{{ item.oxy_number }}</td>
                        <td>{{ item.oxy_name }}</td>
                        <td>
                            <StatusBadge :status="getStatusClass(item.oxy_status)">
                                {{ getStatusText(item.oxy_status) }}
                            </StatusBadge>
                        </td>
                        <td>{{ item.oxy_rental_type || '-' }}</td>
                        <td>{{ item.oxy_rental_status || '-' }}</td>
                        <td>{{ item.oxy_rental_name || '-' }}</td>
                        <td>{{ formatDate(item.oxy_rental_start) || '-' }}</td>
                        <td>{{ formatDate(item.oxy_rental_end) || '-' }}</td>
                        <td @click.stop>
                            <DeleteButton @click="deleteOxygen(item.num)">×</DeleteButton>
                        </td>
                    </StyledTableRow>
                </tbody>
            </OxygenTable>
        </TableContainer>
    </div>
</template>

<script>
import Magnify from 'vue-material-design-icons/Magnify.vue';
import Datepicker from 'vue-datepicker-next';
import ModalPage from '@/components/layout/ModalPage.vue';
import * as RentalCss from '@/assets/styles/rental/RentalCss';
import * as OxygenModal from '@/assets/styles/rental/modal/OxygenModal';
import 'vue-datepicker-next/index.css';

export default {
    name: 'OxygenModal',
    components: {
        Magnify,
        Datepicker,
        ModalPage,
        ...RentalCss,
        ...OxygenModal
    },
    emits: ['close'],
    data() {
        return {
            searchQuery: '',
            oxygenators: [],
            showRegisterModal: false,
            newOxygen: {
                oxy_number: '',
                oxy_name: '',
                oxy_status: 'available',
                oxy_rental_name: '',
                oxy_rental_status: '',
                oxy_rental_start: null,
                oxy_rental_end: null
            },
            showEditModal: false,
            editOxygen: {
                num: null,
                oxy_number: '',
                oxy_name: '',
                oxy_status: '',
                oxy_rental_type: '',
                oxy_rental_name: '',
                oxy_rental_status: '',
                oxy_rental_start: null,
                oxy_rental_end: null
            }
        };
    },
    created() {
        this.fetchOxygenList();
    },
    beforeUnmount() {
        // 모달 컴포넌트가 제거되기 전에 스크롤 복원
        document.body.style.overflow = 'auto';
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
    },

    methods: {
        async downloadOxygenList() {
            try {
                // 크롤링 실행
                const crawlRes = await this.runOxygenCrawlingStep();

                // 크롤링이 취소되었거나 실패한 경우
                if (!crawlRes || !crawlRes.success || crawlRes.cancelled) {
                    return;
                }

                // 크롤링 결과가 없거나 products가 없는 경우
                if (!crawlRes.products || crawlRes.products.length === 0) {
                    await this.$swal.fire('알림', '다운로드할 제품 정보가 없습니다.', 'warning');
                    return;
                }

                // 엑셀 템플릿 다운로드
                const directAxios = (await import('axios')).default;
                const postUrl = `${this.$apiUrls.hanqmedi}/downloadOrderExcelTemplatePrefill`;
                const payload = {
                    companykey: this.$globalState.company_key,
                    products: crawlRes.products.map(p => ({
                        productNumber: p.productNumber,
                        productName: p.productName
                    }))
                };

                const res = await directAxios.post(postUrl, payload, {
                    responseType: 'blob',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 60000
                });

                // 응답 데이터 확인
                if (!res.data) {
                    // 에러 응답인 경우 JSON으로 파싱 시도
                    if (res.data instanceof Blob && res.data.type === 'application/json') {
                        const text = await res.data.text();
                        const errorData = JSON.parse(text);
                        throw new Error(errorData.message || '서버 오류가 발생했습니다.');
                    }
                    throw new Error('다운로드된 파일 데이터가 없습니다.');
                }

                // Blob 크기 확인
                if (res.data.size === 0) {
                    throw new Error('다운로드된 파일이 비어있습니다.');
                }

                // 파일 저장
                const blob = new Blob([res.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const prefillName = '산소발생기 주문서 이관 템플릿.xlsx';

                if (this.$env.isElectron && window.electronAPI?.saveFile) {
                    try {
                        const base64Data = await this.blobToBase64(blob);
                        if (!base64Data || typeof base64Data !== 'string') {
                            throw new Error('파일 변환에 실패했습니다. base64 데이터가 유효하지 않습니다.');
                        }
                        const saveRes = await window.electronAPI.saveFile(prefillName, base64Data);
                        if (!saveRes.success) {
                            throw new Error(saveRes.error || '파일 저장 실패');
                        }
                    } catch (convertError) {
                        console.error('파일 변환/저장 오류:', convertError);
                        throw convertError;
                    }
                } else {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = prefillName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                }

                await this.$swal.fire('다운로드 완료', '산소발생기 주문서 템플릿이 저장되었습니다.', 'success');
            } catch (error) {
                console.error('템플릿 다운로드 오류:', error);
                const errorMessage = error.response?.data?.message || error.message || '엑셀 템플릿 다운로드 중 오류가 발생했습니다.';
                this.$swal.fire('다운로드 실패', errorMessage, 'error');
            }
        },

        // 산소발생기 크롤링 → DB 저장
        async runOxygenCrawlingStep() {
            const company = this.$globalState.company;
            const companykey = this.$globalState.company_key;

            // 회사명 입력
            const { value: companyName, isConfirmed } = await this.$swal.fire({
                title: '산소발생기 목록 가져오기',
                input: 'text',
                inputLabel: '가져올 회사명',
                inputValue: company || '',
                inputPlaceholder: '회사명을 입력하세요',
                showCancelButton: true,
                confirmButtonText: '가져오기',
                cancelButtonText: '취소',
                inputValidator: (value) => {
                    if (!value || !value.trim()) {
                        return '회사명을 입력해주세요.';
                    }
                }
            });
            if (!isConfirmed || !companyName) return { success: false, cancelled: true };

            // 크롤링 실행
            const crawlingResult = await window.electronAPI.startCrawling({
                type: 'navigate-oxygen-list',
                companyName: companyName
            });

            if (!crawlingResult?.success) {
                await this.$swal.fire({
                    title: '오류',
                    text: crawlingResult?.error || '산소발생기 목록 페이지 이동 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
                return { success: false };
            }

            const products = crawlingResult.products || [];
            if (products.length === 0) {
                const msg = crawlingResult.warning
                    ? `${crawlingResult.message}\n경고: ${crawlingResult.warning}`
                    : crawlingResult.message || '가져온 제품 데이터가 없습니다.';
                await this.$swal.fire({
                    title: '알림',
                    html: `<div style="text-align:left;">
                         <p>${msg}</p>
                         <p style="color:#999;font-size:12px;margin-top:10px;">
                           크롤링 결과: ${JSON.stringify(crawlingResult, null, 2).substring(0, 200)}...
                         </p>
                       </div>`,
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return { success: false };
            }

            try {
                const saveResponse = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/bulkRegisterOxygen`,
                    { products, companykey }
                );

                if (saveResponse.data?.success) {
                    const result = saveResponse.data.data;
                    await this.$swal.fire({
                        title: '저장 완료',
                        html: `<div style="text-align:left;">
                             <p><strong>총 ${result.total}개</strong>의 제품 정보를 처리했습니다.</p>
                             <p style="color:#28a745;">✓ 새로 저장: ${result.inserted}개</p>
                             <p style="color:#ffc107;">⊘ 중복/무효: ${result.skipped}개</p>
                           </div>`,
                        icon: 'success',
                        confirmButtonText: '확인'
                    });

                    return {
                        success: true,
                        inserted: result.inserted || 0,
                        total: result.total || 0,
                        products: products
                    };
                } else {
                    throw new Error(saveResponse.data?.message || '데이터 저장에 실패했습니다.');
                }
            } catch (err) {
                await this.$swal.fire({
                    title: '저장 실패',
                    text: err.message || '산소발생기 목록 저장 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
                return { success: false };
            }
        },

        // blobToBase64 헬퍼 함수
        blobToBase64(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },

        async downloadApplicationForm() {
            try {
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/downloadOxygenForm`, {
                    responseType: 'blob' // 파일 다운로드를 위한 설정
                });

                // 파일 다운로드 처리
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', '산소치료 등록신청서.hwp'); // 다운로드될 파일명
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error downloading form:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '다운로드 실패',
                    text: '신청서 양식 다운로드 중 오류가 발생했습니다.'
                });
            }
        },
        openNHISLink() {
            window.open('https://www.nhis.or.kr/nhis/policy/retrieveHomeOxyProductList.do', '_blank');
        },
        closeModal() {
            // 모달 닫을 때 스크롤 복원
            document.body.style.overflow = 'auto';
            this.$emit('close');
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        },
        getStatusClass(status) {
            const statusClassMap = {
                '0': 'available',
                '1': 'rented'
            };
            return statusClassMap[status] || status;
        },
        getStatusText(status) {
            const statusMap = {
                '0': '대여가능',
                '1': '대여중'
            };
            return statusMap[status] || status;
        },
        getRentalStatus(status) {
            const statusMap = {
                '0': '매장',
                '1': '급여',
                '2': '비급여',
                '3': '기초수급자'
            };
            return statusMap[status] || '-';
        },
        async fetchOxygenList() {
            try {
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/getOxygenList`, {
                    params: {
                        search: this.searchQuery,
                        companykey: this.$globalState.company_key
                    }
                });

                if (response.data.success) {
                    this.oxygenators = response.data.data.map(item => ({
                        ...item,
                        oxy_rental_type: item.oxy_rental_type === '0' || item.oxy_rental_type === 0 ? '휴대용' : '가정용',
                        oxy_status: item.oxy_status, // 0: 대여가능, 1: 대여중
                        oxy_rental_status: this.getRentalStatus(item.oxy_rental_status)
                    }));

                    console.log(this.oxygenators);
                } else {
                    console.error('Failed to fetch oxygen list');
                }
            } catch (error) {
                console.error('Error fetching oxygen list:', error);
            }
        },
        async registerOxygen(formData) {
            try {
                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/registerOxygen`, {
                    ...formData,
                    companykey: this.$globalState.company_key
                });

                if (response.data.success) {
                    this.$swal.fire({
                        icon: 'success',
                        title: '등록 완료',
                        text: '산소발생기가 등록되었습니다.'
                    });
                    this.fetchOxygenList();
                } else {
                    this.$swal.fire({
                        icon: 'error',
                        title: '등록 실패',
                        text: response.data.message
                    });
                }
            } catch (error) {
                console.error('Error registering oxygen:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '오류 발생',
                    text: '등록 중 오류가 발생했습니다.'
                });
            }
        },
        resetForm() {
            this.newOxygen = {
                oxy_number: '',
                oxy_name: '',
                oxy_status: 'available',
                oxy_rental_name: '',
                oxy_rental_status: '',
                oxy_rental_start: null,
                oxy_rental_end: null
            };
        },
        async deleteOxygen(oxygenNum) {
            try {
                const result = await this.$swal.fire({
                    title: '삭제 확인',
                    text: '이 산소발생기를 삭제하시겠습니까?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소'
                });

                if (result.isConfirmed) {
                    const response = await this.$axios.delete(`${this.$apiUrls.hanqmedi}/deleteOxygen`, {
                        data: {
                            num: oxygenNum,
                            companykey: this.$globalState.company_key
                        }
                    });

                    if (response.data.success) {
                        this.$swal.fire({
                            icon: 'success',
                            title: '삭제 완료',
                            text: '산소발생기가 삭제되었습니다.'
                        });
                        this.fetchOxygenList(); // 목록 새로고침
                    } else {
                        this.$swal.fire({
                            icon: 'error',
                            title: '삭제 실패',
                            text: response.data.message || '삭제 중 오류가 발생했습니다.'
                        });
                    }
                }
            } catch (error) {
                console.error('Error deleting oxygen:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '오류 발생',
                    text: '삭제 중 오류가 발생했습니다.'
                });
            }
        },
        openEditModal(item) {
            const formatDateForInput = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            };

            // 상태값 확인 로직
            const currentStatus = item.oxy_status === '1' || item.oxy_status === 1 ? '1' : '0';

            const rentalStatusMap = {
                '매장': '0',
                '급여': '1',
                '비급여': '2',
                '기초수급자': '3'
            };

            // 대여자 상태값 확인 로직
            const currentRentalStatus = rentalStatusMap[item.oxy_rental_status] || '0';

            this.$swal.fire({
                title: '산소발생기 수정',
                html: `
                    <div class="swal2-form">
                        <div class="form-group">
                            <label>관리번호</label>
                            <input id="edit_oxy_number" class="swal2-input" value="${item.oxy_number || ''}" placeholder="관리번호 입력">
                        </div>
                        <div class="form-group">
                            <label>제품명</label>
                            <input id="edit_oxy_name" class="swal2-input" value="${item.oxy_name || ''}" placeholder="제품명 입력">
                        </div>
                        <div class="form-group">
                            <label>상태</label>
                            <select id="edit_oxy_status" class="swal2-select">
                                <option value="0" ${currentStatus === '0' ? 'selected' : ''}>대여가능</option>
                                <option value="1" ${currentStatus === '1' ? 'selected' : ''}>대여중</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>휴대용/가정용</label>
                            <select id="edit_oxy_rental_type" class="swal2-select">
                                <option value="0" ${item.oxy_rental_type === '휴대용' ? 'selected' : ''}>휴대용</option>
                                <option value="1" ${item.oxy_rental_type === '가정용' ? 'selected' : ''}>가정용</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>현재 대여자</label>
                            <input id="edit_oxy_rental_name" class="swal2-input" value="${item.oxy_rental_name || ''}" placeholder="대여자명 입력">
                        </div>
                        <div class="form-group">
                            <label>현재 대여자 상태</label>
                            <select id="edit_oxy_rental_status" class="swal2-select">
                                <option value="0" ${currentRentalStatus === '0' ? 'selected' : ''}>매장</option>
                                <option value="1" ${currentRentalStatus === '1' ? 'selected' : ''}>급여</option>
                                <option value="2" ${currentRentalStatus === '2' ? 'selected' : ''}>비급여</option>
                                <option value="3" ${currentRentalStatus === '3' ? 'selected' : ''}>기초수급자</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>대여 시작일</label>
                            <input id="edit_oxy_rental_start" type="date" class="swal2-input" value="${formatDateForInput(item.oxy_rental_start)}">
                        </div>
                        <div class="form-group">
                            <label>대여 종료일</label>
                            <input id="edit_oxy_rental_end" type="date" class="swal2-input" value="${formatDateForInput(item.oxy_rental_end)}">
                        </div>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: '수정',
                cancelButtonText: '취소',
                allowOutsideClick: false,
                stopKeydownPropagation: true,
                scrollbarPadding: false,
                customClass: {
                    container: 'oxygen-modal-container'
                },
                preConfirm: () => {
                    const formData = {
                        num: item.num,
                        oxy_number: document.getElementById('edit_oxy_number').value.trim(),
                        oxy_name: document.getElementById('edit_oxy_name').value.trim(),
                        oxy_status: document.getElementById('edit_oxy_status').value,
                        oxy_rental_type: document.getElementById('edit_oxy_rental_type').value,
                        oxy_rental_name: document.getElementById('edit_oxy_rental_name').value.trim(),
                        oxy_rental_status: document.getElementById('edit_oxy_rental_status').value,
                        oxy_rental_start: document.getElementById('edit_oxy_rental_start').value,
                        oxy_rental_end: document.getElementById('edit_oxy_rental_end').value
                    };

                    // 필수 입력 검증
                    if (!formData.oxy_number || !formData.oxy_name) {
                        this.$swal.showValidationMessage('관리번호, 제품명은 필수 입력항목입니다.');
                        return false;
                    }

                    // 대여중 상태일 때 추가 검증
                    if (formData.oxy_status === '1') {
                        if (!formData.oxy_rental_start || !formData.oxy_rental_end) {
                            this.$swal.showValidationMessage('대여중 상태일 경우 대여기간을 입력해주세요.');
                            return false;
                        }
                    }

                    return formData;
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    this.updateOxygen(result.value);
                }
            });
        },
        openRegisterModal() {
            this.$swal.fire({
                title: '산소발생기 등록',
                html: `
                    <div class="swal2-form">
                        <div class="form-group">
                            <label>관리번호 <span class="required">*</span></label>
                            <input id="oxy_number" class="swal2-input" placeholder="관리번호 입력">
                        </div>
                        <div class="form-group">
                            <label>제품명 <span class="required">*</span></label>
                            <input id="oxy_name" class="swal2-input" placeholder="제품명 입력">
                        </div>
                        <div class="form-group">
                            <label>상태</label>
                            <select id="oxy_status" class="swal2-select">
                                <option value="0">대여가능</option>
                                <option value="1">대여중</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>휴대용/가정용</label>
                            <select id="oxy_rental_type" class="swal2-select">
                                <option value="0">휴대용</option>
                                <option value="1">가정용</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>현재 대여자</label>
                            <input id="oxy_rental_name" class="swal2-input" placeholder="대여자명 입력">
                        </div>
                        <div class="form-group">
                            <label>현재 대여자 상태</label>
                            <select id="oxy_rental_status" class="swal2-input">
                                <option value="0">매장</option>
                                <option value="1">급여</option>
                                <option value="2">비급여</option>
                                <option value="3">기초수급자</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>대여 시작일</label>
                            <input id="oxy_rental_start" type="date" class="swal2-input">
                        </div>
                        <div class="form-group">
                            <label>대여 종료일</label>
                            <input id="oxy_rental_end" type="date" class="swal2-input">
                        </div>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: '등록',
                cancelButtonText: '취소',
                allowOutsideClick: false,
                stopKeydownPropagation: true,
                scrollbarPadding: false,
                customClass: {
                    container: 'oxygen-modal-container'
                },
                preConfirm: () => {
                    const formData = {
                        oxy_number: document.getElementById('oxy_number').value.trim(),
                        oxy_name: document.getElementById('oxy_name').value.trim(),
                        oxy_status: document.getElementById('oxy_status').value,
                        oxy_rental_type: document.getElementById('oxy_rental_type').value,
                        oxy_rental_name: document.getElementById('oxy_rental_name').value.trim(),
                        oxy_rental_status: document.getElementById('oxy_rental_status').value,
                        oxy_rental_start: document.getElementById('oxy_rental_start').value,
                        oxy_rental_end: document.getElementById('oxy_rental_end').value
                    };

                    // 필수 입력 검증
                    if (!formData.oxy_number || !formData.oxy_name) {
                        this.$swal.showValidationMessage('관리번호, 제품명은 필수 입력항목입니다.');
                        return false;
                    }

                    // 대여중 상태일 때 추가 검증
                    if (formData.oxy_status === '1') {
                        if (!formData.oxy_rental_start || !formData.oxy_rental_end) {
                            this.$swal.showValidationMessage('대여중 상태일 경우 대여기간을 입력해주세요.');
                            return false;
                        }
                    }

                    return formData;
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    this.registerOxygen(result.value);
                }
            });
        },
        async updateOxygen(formData) {
            try {
                // 빈 문자열을 null로 변환하는 함수
                const convertEmptyToNull = (value) => {
                    return value === '' ? null : value;
                };

                // 기본 데이터 설정
                const updatedData = {
                    ...formData,
                    oxy_rental_start: convertEmptyToNull(formData.oxy_rental_start),
                    oxy_rental_end: convertEmptyToNull(formData.oxy_rental_end),
                    companykey: this.$globalState.company_key
                };

                // 대여 가능 상태이면서 대여자가 '매장'이 아닐 경우에만 대여 관련 정보 초기화
                if (updatedData.oxy_status === '0' && updatedData.oxy_rental_name !== '매장') {
                    updatedData.oxy_rental_name = null;
                    updatedData.oxy_rental_status = null;
                    updatedData.oxy_rental_type = null;
                    updatedData.oxy_rental_start = null;
                    updatedData.oxy_rental_end = null;
                }

                // 대여자가 '매장'인 경우 대여 기간만 초기화
                if (updatedData.oxy_rental_name === '매장') {
                    updatedData.oxy_rental_status = null;
                    updatedData.oxy_rental_start = null;
                    updatedData.oxy_rental_end = null;
                }

                console.log('Final update data:', updatedData);  // 최종 데이터 로깅

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/updateOxygen`, updatedData);

                if (response.data.success) {
                    this.$swal.fire({
                        icon: 'success',
                        title: '수정 완료',
                        text: '산소발생기 정보가 수정되었습니다.'
                    });
                    this.fetchOxygenList();
                } else {
                    this.$swal.fire({
                        icon: 'error',
                        title: '수정 실패',
                        text: response.data.message || '수정 중 오류가 발생했습니다.'
                    });
                }
            } catch (error) {
                console.error('Error updating oxygen:', error);
                console.error('Error details:', error.response?.data);

                this.$swal.fire({
                    icon: 'error',
                    title: '오류 발생',
                    text: error.response?.data?.message || '수정 중 오류가 발생했습니다.'
                });
            }
        }
    }
};
</script>

<style>
.swal2-form {
    text-align: left;
    padding: 0 20px;
}

.swal2-form .form-group {
    margin-bottom: 10px;
}

.swal2-form label {
    display: block;
    margin-bottom: 3px;
    font-weight: bold;
}

.swal2-form .swal2-input,
.swal2-form .swal2-select {
    width: 100%;
    margin: 3px 0;
    height: 32px;
    padding: 4px 8px;
}

/* SweetAlert2 팝업 자체의 패딩 조정 */
.swal2-popup {
    padding: 1em !important;
}

.swal2-title {
    padding: 0.5em 1em !important;
    margin: 0 !important;
}

.swal2-html-container {
    margin: 0.5em 0 !important;
}

.required {
    color: #ff0000;
    margin-left: 3px;
}

.swal2-validation-message {
    max-width: 450px !important;
    margin: 0 auto !important;
    font-size: 0.9em !important;
    padding: 0.5em !important;
}

.swal2-container .swal2-validation-message {
    margin-top: 0.5em !important;
    margin-bottom: 0.5em !important;
}
</style>


<style scoped>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>